import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';

type Severity = 'info' | 'warn' | 'error' | 'fatal';

export interface DiagnosticEvent {
  category: string;
  event_type: string;
  severity?: Severity;
  message?: string;
  context?: Record<string, unknown> | null;
  error?: unknown;
}

const LAST_EVENT_KEY = 'last_mobile_diagnostic';
const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-mobile-diagnostic`;

let cachedAppInfo: { version?: string; build?: string } | null = null;
let cachedDeviceInfo: Record<string, unknown> | null = null;

const getAppInfo = async () => {
  if (cachedAppInfo) return cachedAppInfo;
  if (!Capacitor.isNativePlatform()) {
    cachedAppInfo = {};
    return cachedAppInfo;
  }
  try {
    const info = await CapApp.getInfo();
    cachedAppInfo = { version: info?.version, build: info?.build };
  } catch {
    cachedAppInfo = {};
  }
  return cachedAppInfo;
};

const getDeviceInfo = async () => {
  if (cachedDeviceInfo) return cachedDeviceInfo;
  // @capacitor/device is optional. Use indirect import so TS/Vite don't try to resolve it at build time.
  try {
    const dynImport = new Function('m', 'return import(m)') as (m: string) => Promise<any>;
    const mod: any = await dynImport('@capacitor/device').catch(() => null);
    if (mod?.Device?.getInfo) {
      const info = await mod.Device.getInfo();
      cachedDeviceInfo = {
        model: info?.model,
        os: info?.operatingSystem,
        os_version: info?.osVersion,
        manufacturer: info?.manufacturer,
        web_view_version: info?.webViewVersion,
      };
      return cachedDeviceInfo;
    }
  } catch {
    // ignore
  }
  cachedDeviceInfo = { user_agent: navigator.userAgent };
  return cachedDeviceInfo;
};

const platformLabel = (): string => {
  if (!Capacitor.isNativePlatform()) {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'web-mobile' : 'web';
  }
  return Capacitor.getPlatform();
};

const errorToParts = (err: unknown): { message?: string; stack?: string } => {
  if (!err) return {};
  if (err instanceof Error) return { message: err.message, stack: err.stack };
  if (typeof err === 'string') return { message: err };
  try {
    return { message: JSON.stringify(err) };
  } catch {
    return { message: String(err) };
  }
};

export async function logEvent(evt: DiagnosticEvent): Promise<void> {
  const severity: Severity = evt.severity ?? 'info';
  const platform = platformLabel();

  // No-op for web "info"/"warn" events to avoid noise. Always send errors.
  if (platform === 'web' && severity !== 'error' && severity !== 'fatal') return;

  try {
    const { message: errMsg, stack } = errorToParts(evt.error);
    const [appInfo, deviceInfo] = await Promise.all([getAppInfo(), getDeviceInfo()]);

    const payload = {
      severity,
      category: evt.category,
      event_type: evt.event_type,
      message: evt.message ?? errMsg,
      error_stack: stack,
      context: evt.context ?? null,
      platform,
      app_version: appInfo.version,
      app_build: appInfo.build,
      device_info: deviceInfo,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };

    try {
      localStorage.setItem(
        LAST_EVENT_KEY,
        JSON.stringify({ ...payload, ts: new Date().toISOString() }),
      );
    } catch {}

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {}

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    try {
      await fetch(FN_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: ctrl.signal,
        keepalive: true,
      });
    } finally {
      clearTimeout(timer);
    }
  } catch {
    // never throw from logger
  }
}

export const getLastDiagnostic = (): unknown => {
  try {
    const raw = localStorage.getItem(LAST_EVENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

let handlersInstalled = false;
export function installGlobalErrorHandlers() {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (e) => {
    logEvent({
      category: 'js_error',
      event_type: 'window_error',
      severity: 'error',
      message: e.message,
      error: e.error ?? e.message,
      context: { filename: e.filename, lineno: e.lineno, colno: e.colno },
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    logEvent({
      category: 'js_error',
      event_type: 'unhandled_rejection',
      severity: 'error',
      error: e.reason,
    });
  });
}
